class FestivePhotoViewer extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.gallery = this.closest('media-gallery');
    this.photos = JSON.parse(this.querySelector('[data-photo-data]').textContent);
    this.dialog = this.querySelector('dialog');
    this.stage = this.querySelector('[data-photo-stage]');
    this.canvas = this.querySelector('[data-photo-canvas]');
    this.image = this.querySelector('[data-photo-image]');
    this.counter = this.querySelector('[data-photo-counter]');
    this.zoomIn = this.querySelector('[data-photo-zoom-in]');
    this.zoomOut = this.querySelector('[data-photo-zoom-out]');
    this.reset = this.querySelector('[data-photo-reset]');
    this.previous = this.querySelector('[data-photo-previous]');
    this.next = this.querySelector('[data-photo-next]');
    this.scale = 1;
    this.point = { x: 0.5, y: 0.5 };
    this.gallery.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-tf-photo-id]');
      if (!trigger || trigger.closest('media-gallery') !== this.gallery || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const index = this.photos.findIndex((photo) => photo.id === trigger.dataset.tfPhotoId);
      if (index < 0) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(index, trigger);
    }, { capture: true, signal });
    this.gallery.addEventListener('keydown', (event) => {
      const trigger = event.target.closest('[data-tf-photo-id]');
      if (!trigger || trigger.tagName === 'BUTTON' || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      trigger.click();
    }, { signal });
    this.querySelector('[data-photo-close]').addEventListener('click', () => this.dialog.close(), { signal });
    this.previous.addEventListener('click', () => this.show(this.index - 1), { signal });
    this.next.addEventListener('click', () => this.show(this.index + 1), { signal });
    this.zoomIn.addEventListener('click', () => this.zoom(this.scale + 0.5), { signal });
    this.zoomOut.addEventListener('click', () => this.zoom(this.scale - 0.5), { signal });
    this.reset.addEventListener('click', () => this.zoom(1), { signal });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      document.body.style.overflow = this.bodyOverflow;
      this.opener?.focus({ preventScroll: true });
    }, { signal });
    this.image.addEventListener('click', (event) => {
      const rect = this.image.getBoundingClientRect();
      this.point = { x: (event.clientX - rect.left) / rect.width, y: (event.clientY - rect.top) / rect.height };
      this.zoom(this.scale > 1 ? 1 : 2);
    }, { signal });
    this.image.addEventListener('keydown', (event) => {
      if (!['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      this.point = { x: 0.5, y: 0.5 };
      this.zoom(this.scale > 1 ? 1 : 2);
    }, { signal });
    this.stage.addEventListener('pointermove', (event) => {
      if (this.scale <= 1 || event.pointerType !== 'mouse') return;
      const rect = this.stage.getBoundingClientRect();
      this.stage.scrollLeft = Math.max(0, this.canvas.offsetWidth - this.stage.clientWidth) * Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      this.stage.scrollTop = Math.max(0, this.canvas.offsetHeight - this.stage.clientHeight) * Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    }, { signal });
    this.image.addEventListener('load', () => this.fit(), { signal });
    this.image.addEventListener('error', () => {
      this.image.hidden = true;
      this.querySelector('[data-photo-error]').hidden = false;
    }, { signal });
    this.observer = new ResizeObserver(() => {
      if (this.dialog.open) this.fit();
    });
    this.observer.observe(this.stage);
  }

  disconnectedCallback() {
    if (this.dialog?.open) {
      this.dialog.close();
      document.body.style.overflow = this.bodyOverflow;
    }
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = null;
  }

  open(index, trigger) {
    this.opener = trigger;
    if (!this.dialog.open) {
      this.bodyOverflow = document.body.style.overflow;
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
    }
    this.show(index);
    this.querySelector('[data-photo-close]').focus({ preventScroll: true });
  }

  show(index) {
    this.index = (index + this.photos.length) % this.photos.length;
    const photo = this.photos[this.index];
    this.scale = 1;
    this.point = { x: 0.5, y: 0.5 };
    this.image.hidden = false;
    this.querySelector('[data-photo-error]').hidden = true;
    this.image.alt = photo.alt;
    this.image.width = photo.width;
    this.image.height = photo.height;
    this.image.src = photo.src;
    this.counter.textContent = `${this.index + 1} / ${this.photos.length}`;
    this.previous.hidden = this.next.hidden = this.photos.length < 2;
    this.fit();
  }

  zoom(scale) {
    this.scale = Math.max(1, Math.min(3, scale));
    if (this.scale === 1) this.point = { x: 0.5, y: 0.5 };
    this.fit();
  }

  fit() {
    const photo = this.photos[this.index];
    if (!photo || !this.dialog.open) return;
    const availableWidth = this.stage.clientWidth;
    const availableHeight = this.stage.clientHeight;
    const fitted = Math.min((availableWidth - 32) / photo.width, (availableHeight - 32) / photo.height, 1);
    const width = photo.width * fitted * this.scale;
    const height = photo.height * fitted * this.scale;
    this.canvas.style.width = `${Math.max(availableWidth, width + 32)}px`;
    this.canvas.style.height = `${Math.max(availableHeight, height + 32)}px`;
    this.image.style.width = `${width}px`;
    this.image.style.height = `${height}px`;
    this.stage.scrollLeft = (this.canvas.offsetWidth - width) / 2 + width * this.point.x - availableWidth / 2;
    this.stage.scrollTop = (this.canvas.offsetHeight - height) / 2 + height * this.point.y - availableHeight / 2;
    this.image.toggleAttribute('data-zoomed', this.scale > 1);
    this.image.setAttribute('aria-label', this.scale > 1 ? this.zoomOut.getAttribute('aria-label') : this.zoomIn.getAttribute('aria-label'));
    this.zoomOut.disabled = this.scale <= 1;
    this.zoomIn.disabled = this.scale >= 3;
    this.reset.disabled = this.scale === 1;
    this.querySelector('[data-photo-zoom-level]').textContent = `${Math.round(this.scale * 100)}%`;
  }
}

if (!customElements.get('tf-photo-viewer')) customElements.define('tf-photo-viewer', FestivePhotoViewer);
